import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StripeLoader from "../../ext/StripeLoader";
import Image from "../../Tags/Image";
import { getOrg } from "../../../apis/ext";
import { useCreateOrg } from "../../ext/useCreateOrg";
import CRM from "../../ext/crm";
import { useStoreIntegrations } from "../../../stores/useStoreIntegrations";
import { useStoreCRMSearch } from "../../../stores/useStoreCRMSearch";
import { Checkbox } from "../../ui/checkbox";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

const LINKEDIN_COMPANY_BASE_URL = "https://www.linkedin.com/company/";
const POLLING_INTERVAL_MS = 5000;

const parseOrgPayload = (response) => {
  console.log("[SingleAccount] raw org payload", response);
  if (!response) return null;
  if (response.data?.data?.organization) return response.data.data;
  if (response.data?.organization) return response.data;
  if (response.data?.data) return response.data.data;
  if (response.data) return response.data;
  return response;
};

const normalizeIdentifier = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    const match = trimmed.match(/linkedin\.com\/company\/([^\/?]+)/i);
    if (match && match[1]) {
      return match[1].toLowerCase();
    }
    return trimmed.toLowerCase();
  }

  return trimmed.toLowerCase();
};

const collectProviderIds = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) return source;
  return [];
};

const gatherIdentifiersFromAccount = (account) => {
  const identifiers = new Set();

  const push = (value) => {
    const normalized = normalizeIdentifier(value);
    if (normalized) {
      identifiers.add(normalized);
    }
  };

  push(account?.company_id);
  push(account?.companyId);
  push(account?.company_linkedin_id);
  push(account?.companyLinkedinId);
  push(account?.companyLinkedinSlug);
  push(account?.company_linkedin_url);
  push(account?.companyLinkedinUrl);
  push(account?.companyLinkedinNumericId);

  collectProviderIds(account?.provider_company_ids?.linkedin).forEach(push);
  collectProviderIds(account?.organization?.provider_company_ids?.linkedin).forEach(push);

  push(account?.organization?.linkedin_url);
  push(account?.organization?.id);

  return Array.from(identifiers);
};

const resolveCompanyIdentifier = (account) => {
  return (
    account?.company_id ||
    account?.companyId ||
    account?.company_linkedin_id ||
    account?.companyLinkedinId ||
    account?.companyLinkedinNumericId ||
    null
  );
};

const resolveLinkedinUrl = (account) => {
  if (account?.organization?.linkedin_url) {
    return account.organization.linkedin_url;
  }

  if (account?.company_linkedin_url) {
    return account.company_linkedin_url;
  }

  if (account?.companyLinkedinUrl) {
    return account.companyLinkedinUrl;
  }

  const identifiers = gatherIdentifiersFromAccount(account);
  const slugCandidate = identifiers.find((value) => value && isNaN(Number(value)));
  if (slugCandidate) {
    return `${LINKEDIN_COMPANY_BASE_URL}${slugCandidate}`;
  }

  const numericCandidate = identifiers.find((value) => value && !isNaN(Number(value)));
  if (numericCandidate) {
    return `${LINKEDIN_COMPANY_BASE_URL}${numericCandidate}`;
  }

  return null;
};

const resolveCompanyName = (account, organization) => {
  return (
    organization?.name ||
    organization?.legal_name ||
    organization?.display_name ||
    account?.company_name ||
    account?.companyName ||
    "Organization"
  );
};

const resolveIndustry = (account, organization) => {
  return (
    organization?.industry || organization?.category || account?.industry || account?.company_industry || ""
  );
};

const resolveLogo = (account, organization) => {
  return organization?.logo_url || organization?.logo || account?.company_logo || "/images/ext/user-gd.svg";
};

const SingleAccount = ({
  account,
  workspaceId,
  activeWorkspace,
  accountKey,
  orgSearchStatus = {},
  onOrganizationResolved,
  isSelected = false,
  onSelectChange,
}) => {
  const captureEvent = useCaptureEvent();
  const [orgData, setOrgData] = useState(() =>
    account?.organization
      ? {
          hasPendingEnrichmentAction: account?.hasPendingEnrichmentAction ?? false,
          organization: account.organization,
        }
      : null
  );
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef(null);
  const accountKeyRef = useRef(accountKey);
  const hasTriggeredRef = useRef(false);
  const isMountedRef = useRef(true);
  const lastReportedOrgRef = useRef(null);

  const { activeCRMIntegration } = useStoreIntegrations();
  const { getCRMSearchRecordById, isCRMBulkSearchLoading } = useStoreCRMSearch();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const updateOrgData = useCallback((payload) => {
    if (!isMountedRef.current || !payload?.organization) {
      return;
    }

    setOrgData((prev) => {
      const next = {
        hasPendingEnrichmentAction: !!payload.hasPendingEnrichmentAction,
        organization: payload.organization,
      };

      if (
        prev &&
        prev.organization?.id === next.organization?.id &&
        prev.hasPendingEnrichmentAction === next.hasPendingEnrichmentAction
      ) {
        return prev;
      }

      return next;
    });
  }, []);

  const pollOrg = useCallback(
    async (organizationId) => {
      if (!organizationId) {
        stopPolling();
        if (isMountedRef.current) {
          setIsPolling(false);
        }
        return;
      }

      try {
        const response = await getOrg({
          id: organizationId,
          options: workspaceId ? { headers: { "X-Workspace-Id": workspaceId } } : undefined,
        });

        const payload = parseOrgPayload(response);
        console.log("[SingleAccount] pollOrg normalized payload", payload);
        updateOrgData(payload);

        if (!payload?.hasPendingEnrichmentAction) {
          stopPolling();
          if (isMountedRef.current) {
            setIsPolling(false);
          }
        }
      } catch (err) {
        console.error("Polling organization failed", err);
        stopPolling();
        if (isMountedRef.current) {
          setIsPolling(false);
          setError("Unable to fetch organization details");
        }
      }
    },
    [stopPolling, updateOrgData, workspaceId]
  );

  const startPolling = useCallback(
    (organizationId) => {
      if (!organizationId) {
        return;
      }

      stopPolling();
      if (isMountedRef.current) {
        setIsPolling(true);
      }
      pollOrg(organizationId);
      pollingRef.current = setInterval(() => pollOrg(organizationId), POLLING_INTERVAL_MS);
    },
    [pollOrg, stopPolling]
  );

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    if (accountKey !== accountKeyRef.current) {
      accountKeyRef.current = accountKey;
      hasTriggeredRef.current = false;
      stopPolling();
      if (isMountedRef.current) {
        setIsPolling(false);
      }
      setError(null);
      setOrgData(
        account?.organization
          ? {
              hasPendingEnrichmentAction: account?.hasPendingEnrichmentAction ?? false,
              organization: account.organization,
            }
          : null
      );
    }
  }, [account, accountKey, stopPolling]);

  const handleCreateSuccess = useCallback(
    (response) => {
      const payload = parseOrgPayload(response);
      console.log("[SingleAccount] createOrg success", payload);

      updateOrgData(payload);

      if (isMountedRef.current) {
        setError(null);
      }

      if (payload?.organization?.id) {
        if (payload?.hasPendingEnrichmentAction) {
          startPolling(payload.organization.id);
        } else {
          stopPolling();
          if (isMountedRef.current) {
            setIsPolling(false);
          }
        }
      }
    },
    [setError, startPolling, stopPolling, updateOrgData]
  );

  const handleCreateError = useCallback(
    (err) => {
      console.error("Create organization failed", err);
      if (isMountedRef.current) {
        setError("Unable to create organization");
        setIsPolling(false);
      }
    },
    [setError, setIsPolling]
  );

  const { mutate: createOrganization, isLoading: isCreatingOrg } = useCreateOrg({
    workspaceId,
    onSuccess: handleCreateSuccess,
    onError: handleCreateError,
  });

  const accountOrganization = account?.organization || null;
  const accountPending = account?.hasPendingEnrichmentAction ?? false;

  useEffect(() => {
    if (!orgData?.organization?.id) {
      return;
    }

    const lastReported = lastReportedOrgRef.current;
    if (
      lastReported &&
      lastReported.organization?.id === orgData.organization.id &&
      lastReported.hasPendingEnrichmentAction === orgData.hasPendingEnrichmentAction
    ) {
      return;
    }

    lastReportedOrgRef.current = orgData;
    onOrganizationResolved?.(orgData, accountKey);
  }, [accountKey, onOrganizationResolved, orgData]);

  useEffect(() => {
    if (!accountOrganization) {
      return;
    }

    const payload = {
      hasPendingEnrichmentAction: accountPending,
      organization: accountOrganization,
    };

    updateOrgData(payload);

    if (payload.hasPendingEnrichmentAction && payload.organization?.id) {
      startPolling(payload.organization.id);
    } else {
      stopPolling();
      if (isMountedRef.current) {
        setIsPolling(false);
      }
    }
  }, [accountOrganization, accountPending, startPolling, stopPolling, updateOrgData]);

  useEffect(() => {
    if (accountOrganization || !workspaceId || hasTriggeredRef.current) {
      return;
    }

    const linkedinUrl = resolveLinkedinUrl(account);
    const companyIdentifier = resolveCompanyIdentifier(account);
    const resolvedIdentifier = normalizeIdentifier(linkedinUrl || companyIdentifier);

    if (!linkedinUrl && !companyIdentifier) {
      setError("LinkedIn company identifier unavailable");
      return;
    }

    if (!resolvedIdentifier) {
      setError("Unable to resolve organization identifier");
      return;
    }

    hasTriggeredRef.current = true;
    setError(null);

    createOrganization({
      linkedinUrl,
      companyId: companyIdentifier,
      companyIdentifier,
    });
  }, [account, accountOrganization, createOrganization, workspaceId]);

  const organization = orgData?.organization;
  const isLoading = isCreatingOrg || isPolling;
  const linkedinUrl = useMemo(
    () => resolveLinkedinUrl({ ...account, organization }),
    [account, organization]
  );
  const companyName = resolveCompanyName(account, organization);
  const industry = resolveIndustry(account, organization);
  const logo = resolveLogo(account, organization);

  const crmSearchRecord = organization?.id ? getCRMSearchRecordById(organization.id) : null;
  const crmRecords = crmSearchRecord?.records;
  const orgSearchEntry = organization?.id ? orgSearchStatus?.[organization.id] : null;
  const isCRMBulkSearchPending = orgSearchEntry
    ? !orgSearchEntry.completed
    : Boolean(organization?.id && isCRMBulkSearchLoading && !crmSearchRecord);

  const crmRecordData = useMemo(() => {
    if (!organization) {
      return null;
    }

    const name = organization.name || account?.company_name || account?.companyName || "";

    return {
      id: organization.id,
      organization_id: organization.id,
      name,
      company: name,
      domain: organization.domain,
      website: organization.domain,
      linkedin_url: organization.linkedin_url,
      company_linkedin_url: organization.linkedin_url,
    };
  }, [account?.companyName, account?.company_name, organization]);

  console.log("crmRecords in accoutns", crmRecords);

  return (
    <div className="border p-3 rounded-xl bg-background flex flex-col gap-3 min-h-[88px] relative">
      {isPolling && (
        <div className="absolute left-3 right-3 top-0 overflow-hidden h-0.5">
          <StripeLoader />
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="flex items-start space-x-3">
          <div className="inline-flex items-center space-x-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => {
                onSelectChange?.(checked === true);
                captureEvent("EXC_SingleAccount_SelectAccount", {
                  account_key: accountKey,
                  company_name: companyName,
                  is_selected: checked === true,
                });
              }}
              disabled={!organization?.id}
            />
            <Image nobaseurl={true} src={logo} width={40} height={40} className="rounded-full w-10 h-10 no-invert" />
            <div>
              {linkedinUrl ? (
                <a
                  className="text-[16px] text-gray-900 block font-bold"
                  href={linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    captureEvent("EXC_SingleAccount_ViewCompany", {
                      account_key: accountKey,
                      company_name: companyName,
                      linkedin_url: linkedinUrl,
                    });
                  }}
                >
                  {companyName}
                </a>
              ) : (
                <span className="text-[16px] text-gray-900 block font-bold">{companyName}</span>
              )}
              {industry && <span className="text-[12px] text-gray-900 block mt-0.5">{industry}</span>}
              {/* {organization?.id && (
                <span className="text-[11px] text-gray-600 block mt-1">Org ID: {organization.id}</span>
              )} */}
              {error && <span className="text-[11px] text-red-500 block mt-1">{error}</span>}
            </div>
          </div>
        </div>
      </div>
      {organization?.id && activeWorkspace && activeCRMIntegration?.id && (
        <div className="border-t pt-3 mt-1">
          <CRM
            activeWorkspace={activeWorkspace}
            recordData={crmRecordData || {}}
            activeCrm={activeCRMIntegration?.id}
            type="company"
            className="relative inline-block"
            crmSearchData={crmRecords}
            isCRMBulkSearchPending={isCRMBulkSearchPending}
            isBulkEnrichment
            source="bulk-accounts"
          />
        </div>
      )}
    </div>
  );
};

export default SingleAccount;
